import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { User } from 'src/app/models/User';
import { WalletBilling } from 'src/app/models/Wallet';
import { WalletService } from 'src/app/shared/services/wallet.service';

import { Price } from './../../../models/Price';
import { ConfirmationService } from './../../../shared/components/confirmation-modal/confirmation.service';
import { ToastService } from './../../../shared/components/toast/toast.service';
import { AuthService } from './../../../shared/services/auth.service';
import { RechargesService } from './../../../shared/services/recharges.service';
import { UserService } from './../../../shared/services/user.service';
import { MenuService } from './../../layout/menu/menu.service';
import { RechargeValidators } from './recharge.validators';

interface PagePrice {
  startQuantity: number;
  endQuantity: number;
  price: number;
}
import { AccountVersionService } from 'src/app/shared/services/account-version.service';

/** O que o gateway exige do pagador — e que o membro não pode preencher. */
const MEMBER_BILLING_FIELDS: (keyof User)[] = ['document', 'phone', 'postcode', 'street', 'number', 'district', 'city', 'region'];

@Component({
  selector: 'app-recharge',
  templateUrl: './recharge.component.html',
  styleUrls: ['./recharge.component.scss'],
})
export class RechargeComponent implements OnInit, OnDestroy {
  credits = 100;

  user: User;
  target: string = 'sms'; // default
  price: number;
  total: number;
  prices: Price[];
  pagePrices: PagePrice[] = [];

  isCnpj: boolean = false;
  oldCNPJCPF: string = '';

  showAddressTab: boolean;
  showAddress: boolean;
  tab: string;

  /** Compra em curso — o botão Pagar carrega enquanto o backend responde. */
  isSaving = false;

  /**
   * Quem paga cada produto (`/wallet/billing`, T.9). Quem já paga SMS ou ligação pela carteira não
   * compra pacote desse produto: o backend recusa com 400 (front-contrato-r3 §6), e a tela avisa
   * antes, no primeiro passo, em vez de no Pagar. Sem resposta (`null`) a tela segue como antes e
   * o 400 do backend segura.
   */
  billing: WalletBilling | null = null;
  tabs = [
    { id: 'credits', text: 'Créditos' },
    { id: 'address', text: 'Endereço' },
    { id: 'payment', text: 'Pagamento' },
  ];

  paymentType = 'pix';

  modal: any;
  issuers: any[];
  pixImage: string;
  pixCode: string;
  rechargeForm: UntypedFormGroup;
  subscriptions: Subscription[] = [];

  @ViewChild('inputCredits') inputCredits: ElementRef;
  @ViewChild('document') inputDocument: ElementRef<HTMLElement>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: UntypedFormBuilder,
    private menuService: MenuService,
    private modalService: NgbModal,
    private authService: AuthService,
    private toastService: ToastService,
    private rechargesService: RechargesService,
    private confirmationService: ConfirmationService,
    private walletService: WalletService,
    private accountVersionService: AccountVersionService,
    private userService: UserService
  ) { }

  ngOnInit(): void {
    this.tab = 'credits';
    this.user = this.authService.getUser();
    this.showAddress = false;
    this.showAddressTab = !this.user.postcode;

    this.route.queryParams.subscribe(params => {
      if (params['target']) {
        this.target = params['target'];
      }
    });

    this.subscriptions.push(
      this.authService.userChanged.subscribe(() => {
        this.ngOnDestroy();
        this.ngOnInit();
      })
    );

    if (!this.showAddressTab) {
      this.tabs = this.tabs.filter((t) => t.id !== 'address');
    }

    this.rechargeForm = this.fb.group({
      name: [
        this.user.name,
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(25),
        ],
      ],
      target: [this.target, Validators.required],
      email: [this.user.email, Validators.required],
      credits: [this.credits, Validators.required],
      payment_type: ['', Validators.required],
      phone: [this.user.phone, [Validators.required, Validators.minLength(10)]],
      document: [
        '',
        [Validators.required, RechargeValidators.documentValidator()],
      ],
      postcode: [
        this.user.postcode,
        [Validators.required, Validators.minLength(9), Validators.maxLength(9)],
      ],
      street: [
        this.user.street,
        [Validators.required, Validators.minLength(3)],
      ],
      number: [this.user.number, Validators.required],
      complement: [this.user.complement],
      district: [
        this.user.district,
        [Validators.required, Validators.minLength(3)],
      ],
      city: [this.user.city, Validators.required],
      region: [this.user.region, Validators.required],
      card_number: ['', Validators.required],
      due_date: ['', [Validators.required, Validators.minLength(7)]],
      cvv: ['', Validators.required],
    });

    // O documento vem cru do backend; aplica a máscara para o campo abrir
    // já formatado e consistente com o que o usuário digita.
    this.oldCNPJCPF = this.maskDocument(this.user?.document ?? '');
    this.rechargeForm.patchValue({ document: this.oldCNPJCPF });

    if (this.isMember) {
      MEMBER_BILLING_FIELDS.forEach((field) => this.rechargeForm.get(field)?.disable({ emitEvent: false }));
      this.rechargeForm.get('complement')?.disable({ emitEvent: false });
      this.reloadAccountBilling();
    }

    this.subscriptions.push(
      this.rechargeForm.get('credits').valueChanges.subscribe((value) => {
        this.creditsValuesChange(value);
      })
    );

    this.subscriptions.push(
      this.walletService.getBilling()
        .pipe(catchError(() => of(null)))
        .subscribe((billing) => (this.billing = billing))
    );

    this.subscriptions.push(
      this.rechargesService.price().subscribe((prices) => {
        this.pagePrices = [];
        this.prices = prices;
        this.price = this.getPrice();
        this.total = this.price * this.credits;

        if (prices.length === 1 && prices[0].quantity === 0) {
          return;
        }

        this.renderPagePrices();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  /** O produto é cobrado em R$ pela carteira nesta conta: não há pacote dele para comprar. */
  isWalletBilled(target: string): boolean {
    return this.billing?.[target] === 'wallet';
  }

  get targetWalletBilled(): boolean {
    return this.isWalletBilled(this.target);
  }

  /**
   * Multiusuários (CU-17tm466h1cm, decisão Q12): documento e endereço de
   * cobrança são da conta e só o dono altera. O membro compra com o que está
   * cadastrado — os campos aparecem travados — e, faltando algum, a compra nem
   * começa: o recado é pedir ao proprietário. O backend aplica a mesma regra.
   */
  get isMember(): boolean {
    return !!this.authService.currentMember;
  }

  get memberMissingBilling(): boolean {
    return this.isMember && MEMBER_BILLING_FIELDS.some((field) => !String(this.user?.[field] ?? '').trim());
  }

  /**
   * A sessão guarda a conta como estava no login. Se o dono completou o
   * cadastro depois, o membro seguiria travado em `memberMissingBilling` até
   * sair e entrar de novo — então relê a conta ao abrir a recarga.
   */
  private reloadAccountBilling(): void {
    this.subscriptions.push(
      this.userService.findById(this.user.id)
        .pipe(catchError(() => of(null)))
        .subscribe((account) => {
          if (!account) {
            return;
          }

          this.user = { ...this.user, ...account };
          this.oldCNPJCPF = this.maskDocument(this.user.document ?? '');
          const billing: Partial<Record<keyof User, any>> = { complement: this.user.complement };
          MEMBER_BILLING_FIELDS.forEach((field) => billing[field] = this.user[field]);
          billing.document = this.oldCNPJCPF;

          this.rechargeForm.patchValue(billing, { emitEvent: false });
        })
    );
  }

  renderPagePrices(): void {
    this.pagePrices = [];
    this.prices[this.target].forEach((p, idx) => {
      let start = this.credits;
      if (idx > 0) {
        start = this.prices[this.target][idx - 1].quantity + 1;
      }

      this.pagePrices.push({
        startQuantity: start,
        endQuantity: p.quantity,
        price: p.price,
      });
    });
  }

  creditsValuesChange(value: number): void {

    if(!value) {
      value = this.credits;
    }

    this.credits = +value;

    this.price = this.getPrice();

    console.log(this.price);

    this.total = +value * this.price;

  }

  getPrice(): number {

    const _prices = this.prices[this.target];

    const price = _prices.find(p => p.quantity >= this.credits);

    if (price) {
      return price.price;
    }

    if (_prices?.length > 0) {
      if (this.credits > _prices[_prices.length - 1].quantity) {
        return _prices[_prices.length - 1].price;
      }
    }
  }

  setDocumentFocus(): void {
    if (!this.menuService.isSmallDevice) {
      setTimeout(() => this.inputDocument?.nativeElement?.focus(), 200);
    }
  }

  setCreditsFocus(): void {
    if (!this.menuService.isSmallDevice) {
      setTimeout(() => this.inputCredits?.nativeElement?.focus(), 200);
    }
  }

  togglePaymentType(paymentType: string): void {
    this.paymentType = paymentType;
    this.rechargeForm.patchValue({ payment_type: paymentType });
    this.setDocumentFocus();
  }

  targetChanged(evt): void {
    this.rechargeForm.patchValue({ target: evt.target.value });
    this.creditsValuesChange(null);
    this.renderPagePrices();
  }

  changePhone(phone: string): void {
    this.rechargeForm.patchValue({ phone });
  }

  changeCardNumber(cardNumber: any): void {
    this.rechargeForm.patchValue({ card_number: cardNumber });
  }

  formatDueDate(event: any): void {
    if (event.code === 'Backspace' || event.code === 'Tab') {
      return;
    }

    const dueDate = this.rechargeForm
      .get('due_date')
      ?.value.match(/\d+/g)
      ?.join([]);
    if (!dueDate) {
      this.rechargeForm.patchValue({ due_date: '' });
      return;
    }

    let formattedDueDate = '';
    for (const digit of dueDate) {
      formattedDueDate += digit;
      if (formattedDueDate.length === 2) {
        formattedDueDate += '/';
      }
    }

    this.rechargeForm.patchValue({ due_date: formattedDueDate });
  }

  backClick(): void {
    const index = this.tabs.findIndex((t) => t.id === this.tab);

    if (index === 0) {
      this.showAddress = false;
    }

    if (index > 0) {
      this.tab = this.tabs[index - 1].id;
    } else {
      window.history.back();
    }
  }

  nextClick(modal: any): void {
    this.modal = modal;
    if (
      (this.tab === 'credits' && !this.showAddressTab) ||
      this.tab === 'address'
    ) {
      this.tab = 'payment';
      this.setDocumentFocus();
      return;
    }

    if (this.tab === 'credits') {
      this.tab = 'address';
      return;
    }

    this.buy(null);
  }

  isFormValid(): boolean {
    if (this.tab === 'credits') {
      return this.credits >= 100 && !this.targetWalletBilled && !this.memberMissingBilling;
    }

    const rechargeForm = this.rechargeForm.getRawValue();

    if (this.tab === 'address') {
      return (
        this.showAddress &&
        rechargeForm.number > 0 &&
        rechargeForm.street.trim() &&
        rechargeForm.district.trim() &&
        rechargeForm.city.trim() &&
        rechargeForm.region.trim()
      );
    }

    if (this.tab === 'payment') {
      if (this.paymentType === 'pix') {
        return (
          rechargeForm.document?.trim() &&
          !this.rechargeForm.get('document').errors
        );
      }

      return (
        +rechargeForm.cvv &&
        rechargeForm.name?.trim() &&
        rechargeForm.document?.trim() &&
        rechargeForm.card_number?.trim() &&
        rechargeForm.due_date?.trim() &&
        rechargeForm.due_date.trim().length === 7
      );
    }
  }

  buy(event: any): void {
    if (event) {
      event.preventDefault();
    }

    if (this.targetWalletBilled || this.memberMissingBilling) {
      return;
    }

    if (this.tab === 'credits' && this.credits > 100) {
      if (this.tabs.length === 3) {
        this.tab = 'address';
      } else {
        this.tab = 'payment';
      }
      return;
    }

    if (!this.isFormValid()) {
      return;
    }

    const rechargeForm = this.rechargeForm.getRawValue();

    rechargeForm.payment_type = this.paymentType;
    rechargeForm.card_number = rechargeForm.card_number.replace(/\D/g, '');
    rechargeForm.document = rechargeForm.document.replace(/\D/g, '');

    this.confirmationService.show(
      `Confirma a compra de ${this.credits} créditos no valor de R$ ${this.total
        .toFixed(2)
        .replace('.', ',')}?`,
      () => this.registerBuy(rechargeForm)
    );
  }

  pixCopy(pixCode: string): void {
    navigator.clipboard.writeText(pixCode);
    this.toastService.success('Código PIX copiado para a área de transferência!');
  }

  registerBuy(recharge: any): void {
    this.isSaving = true;
    this.rechargesService.buy(recharge).subscribe({
      next: res => {
        this.isSaving = false;
        let message = 'Créditos incluídos com sucesso.';
        if (!res.confirmated_at) {
          message +=
            ' Ainda não foram confirmados, será necessário aguardar alguns minutos!';
        }

        this.toastService.success(message);

        if (this.paymentType === 'pix') {
          // Pendurado no body, fora do shell: sem a classe de versão o sf-btn primário fica verde
          // no internacional.
          this.modalService.open(this.modal, { windowClass: this.accountVersionService.themeClass });
          this.pixImage = res.url_qrcode_pix;
          this.pixCode = res.pix_code;
        }

        this.updateUser();

        this.router.navigate(['/recharges']);
      },
      error: res => {
        this.isSaving = false;

        if (res.error?.message) {
          this.toastService.error(res.error.message);
        }
      }
    });
  }

  updateUser(): void {
    const rechargeForm = this.rechargeForm.getRawValue();

    this.user.document = rechargeForm.document;
    this.user.city = rechargeForm.city;
    this.user.complement = rechargeForm.complement;
    this.user.district = rechargeForm.district;
    this.user.number = rechargeForm.number;
    this.user.postcode = rechargeForm.postcode;
    this.user.region = rechargeForm.region;
    this.user.street = rechargeForm.street;
    this.user.phone = rechargeForm.phone;

    if (this.authService.getSimulatedUser()) {
      this.authService.setSimulatedUser(this.user);
    } else {
      this.authService.setUser(this.user);
    }
  }

  formatCpfCnpj(e) {
    // Ao apagar não remascara, senão o separador removido volta na hora.
    const deleting = e.target.value.length < this.oldCNPJCPF.length;
    const value = deleting ? e.target.value : this.maskDocument(e.target.value);

    this.oldCNPJCPF = value;
    e.target.value = value;

    // O ValueAccessor já gravou o valor sem máscara antes deste handler,
    // então o control precisa ser sobrescrito com o valor formatado.
    const control = this.rechargeForm.get('document');
    control.setValue(value, { emitEvent: false });
    control.markAsDirty();
  }

  private maskDocument(value: string): string {
    const cleanedValue = (value ?? '').toString().replace(/\D/g, '').slice(0, 14);

    if (!cleanedValue) {
      return '';
    }

    const masked = cleanedValue.length <= 11
      ? cleanedValue.replace(/(\d{0,3})(\d{0,3})(\d{0,3})(\d{0,2})/, '$1.$2.$3-$4')
      : cleanedValue.replace(/(\d{0,2})(\d{0,3})(\d{0,3})(\d{0,4})(\d{0,2})/, '$1.$2.$3/$4-$5');

    return masked.replace(/[.\/-]+$/, '');
  }

}
